import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  CalendarIcon,
  ClockIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CubeIcon,
  BellIcon,
  CreditCardIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DashboardStats {
  todayRevenue: number;
  monthlyRevenue: number;
  activeJobs: number;
  totalCustomers: number;
  pendingInvoices: number;
  totalEmployees: number;
  lowStockItems: number;
  todayAppointments: number;
  completedJobsToday: number;
  averageServiceTime: number;
  customerSatisfaction: number;
  recurringCustomers: number;
}

interface RecentActivity {
  id: string;
  type: 'job_created' | 'job_completed' | 'invoice_paid' | 'customer_added' | 'appointment_booked';
  title: string;
  description: string;
  timestamp: string;
  status?: string;
  amount?: number;
}

interface TopService {
  name: string;
  count: number;
  revenue: number;
}

interface UpcomingAppointment {
  id: string;
  customerName: string;
  vehicle: string;
  service: string;
  time: string;
  duration: string;
}

const GarageOwnerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [topServices, setTopServices] = useState<TopService[]>([]);
  const [appointments, setAppointments] = useState<UpcomingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('GarageOwnerDashboard: Starting to fetch dashboard data');
      
      // Fetch real data from API endpoints
      const [jobCardStats, inventoryStats, invoiceStats, paymentStats] = await Promise.all([
        apiClient.get('/job-cards/stats').catch(err => {
          console.error('Failed to fetch job card stats:', err);
          return { data: { stats: {} } };
        }),
        apiClient.get('/inventory/stats').catch(err => {
          console.error('Failed to fetch inventory stats:', err);
          return { data: { stats: {} } };
        }),
        apiClient.get('/invoices/stats').catch(err => {
          console.error('Failed to fetch invoice stats:', err);
          return { data: { data: {} } };
        }),
        apiClient.get('/payments/stats').catch(err => {
          console.error('Failed to fetch payment stats:', err);
          return { data: { stats: {} } };
        })
      ]);

      // Combine stats from different endpoints
      const combinedStats = {
        todayRevenue: paymentStats.data?.stats?.todayRevenue || 0,
        monthlyRevenue: paymentStats.data?.stats?.monthlyRevenue || 0,
        activeJobs: jobCardStats.data?.stats?.activeJobs || 0,
        totalCustomers: jobCardStats.data?.stats?.totalCustomers || 0,
        pendingInvoices: invoiceStats.data?.data?.pendingInvoices || 0,
        totalEmployees: 8, // This might come from a different endpoint
        lowStockItems: inventoryStats.data?.stats?.lowStockItems || 0,
        todayAppointments: jobCardStats.data?.stats?.todayAppointments || 0,
        completedJobsToday: jobCardStats.data?.stats?.completedJobsToday || 0,
        averageServiceTime: jobCardStats.data?.stats?.averageServiceTime || 0,
        customerSatisfaction: 4.7, // This might come from reviews endpoint
        recurringCustomers: jobCardStats.data?.stats?.recurringCustomers || 0,
      };
      
      setStats(combinedStats);

      // Fetch recent activities (this might need a separate endpoint)
      try {
        const activitiesResponse = await apiClient.get('/admin/activity-logs', {
          params: { limit: 5, sort: 'desc' }
        }).catch(err => {
          console.error('Failed to fetch activities:', err);
          return { data: { data: [] } };
        });
        
        if (activitiesResponse.data?.data) {
          const formattedActivities = activitiesResponse.data.data.map((activity: any) => ({
            id: activity.id?.toString() || '',
            type: activity.action || 'job_created',
            title: activity.title || activity.description || '',
            description: activity.details || '',
            timestamp: formatTimestamp(activity.created_at),
            status: activity.status,
            amount: activity.amount
          }));
          setActivities(formattedActivities);
        }
      } catch (err) {
        console.error('Failed to fetch activities:', err);
        // Set some default activities if API fails
        setActivities([]);
      }

      // Fetch top services (might need custom endpoint)
      try {
        const servicesResponse = await apiClient.get('/job-cards/top-services', {
          params: { period: selectedPeriod }
        }).catch(err => {
          console.error('Failed to fetch top services:', err);
          return { data: { data: [] } };
        });
        
        if (servicesResponse.data?.data) {
          setTopServices(servicesResponse.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch top services:', err);
        // Set mock data for now
        setTopServices([
          { name: 'Oil Change', count: 45, revenue: 2250 },
          { name: 'Brake Service', count: 32, revenue: 4800 },
          { name: 'Tire Rotation', count: 28, revenue: 1400 },
          { name: 'Engine Diagnostics', count: 18, revenue: 1800 },
          { name: 'AC Service', count: 15, revenue: 2250 }
        ]);
      }

      // Fetch today's appointments
      try {
        const appointmentsResponse = await apiClient.get('/job-cards', {
          params: { 
            status: 'scheduled',
            date: new Date().toISOString().split('T')[0]
          }
        }).catch(err => {
          console.error('Failed to fetch appointments:', err);
          return { data: { data: [] } };
        });
        
        if (appointmentsResponse.data?.data) {
          const formattedAppointments = appointmentsResponse.data.data.map((jobCard: any) => ({
            id: jobCard.id?.toString() || '',
            customerName: jobCard.customer?.name || 'Unknown',
            vehicle: `${jobCard.vehicle?.make || ''} ${jobCard.vehicle?.model || ''} ${jobCard.vehicle?.year || ''}`.trim(),
            service: jobCard.services?.[0]?.name || 'Service',
            time: formatTime(jobCard.scheduled_at),
            duration: jobCard.estimated_duration || '1 hour'
          }));
          setAppointments(formattedAppointments);
        }
      } catch (err) {
        console.error('Failed to fetch appointments:', err);
        setAppointments([]);
      }

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set default values if everything fails
      setStats({
        todayRevenue: 0,
        monthlyRevenue: 0,
        activeJobs: 0,
        totalCustomers: 0,
        pendingInvoices: 0,
        totalEmployees: 0,
        lowStockItems: 0,
        todayAppointments: 0,
        completedJobsToday: 0,
        averageServiceTime: 0,
        customerSatisfaction: 0,
        recurringCustomers: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Helper function to format time
  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} KWD`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'job_completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'job_created':
        return <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600" />;
      case 'invoice_paid':
        return <CreditCardIcon className="w-5 h-5 text-green-600" />;
      case 'customer_added':
        return <UsersIcon className="w-5 h-5 text-purple-600" />;
      case 'appointment_booked':
        return <CalendarIcon className="w-5 h-5 text-orange-600" />;
      default:
        return <DocumentTextIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const renderRevenueChart = () => {
    const data = {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [
        {
          label: 'Daily Revenue',
          data: [180, 220, 195, 245, 210, 290, 245],
          borderColor: '#008B8B',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'This Week Revenue',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value: any) {
              return value + ' KWD';
            },
          },
        },
      },
    };

    return <Line data={data} options={options} />;
  };

  const renderServiceChart = () => {
    const data = {
      labels: topServices.map(s => s.name),
      datasets: [
        {
          data: topServices.map(s => s.count),
          backgroundColor: [
            '#008B8B',
            '#FF8C00',
            '#F59E0B',
            '#10B981',
            '#8B5CF6',
          ],
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: 'Top Services This Month',
        },
      },
    };

    return <Doughnut data={data} options={options} />;
  };

  const renderEmployeePerformance = () => {
    const data = {
      labels: ['Ahmed', 'Hassan', 'Mohammed', 'Ali', 'Khalid'],
      datasets: [
        {
          label: 'Jobs Completed',
          data: [12, 19, 15, 8, 10],
          backgroundColor: '#008B8B',
        },
        {
          label: 'Revenue Generated',
          data: [450, 680, 520, 290, 380],
          backgroundColor: '#FF8C00',
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Employee Performance This Week',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    };

    return <Bar data={data} options={options} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="garage_owner"
        showNotifications={true}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Owner'}
              </h1>
              <p className="text-gray-600 mt-1">
                {user?.garage?.name || 'Al-Salam Auto Service'} - Dashboard Overview
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
              <button className="bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] text-sm">
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* Alerts Section */}
        {stats && stats.lowStockItems > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">{stats.lowStockItems} items</span> are running low on stock. 
                <button 
                  onClick={() => navigate('/dashboard/inventory')}
                  className="ml-2 text-yellow-900 underline font-medium"
                >
                  View Inventory
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Revenue</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {formatCurrency(stats?.todayRevenue || 0)}
                </p>
                <div className="mt-2 flex items-center">
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600 font-medium">12%</span>
                  <span className="text-sm text-gray-500 ml-1">vs yesterday</span>
                </div>
              </div>
              <div className="p-4 bg-[#008B8B]/10 rounded-xl">
                <CurrencyDollarIcon className="w-8 h-8 text-[#008B8B]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#FF8C00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Jobs</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {stats?.activeJobs || 0}
                </p>
                <div className="mt-2">
                  <span className="text-sm text-gray-500">
                    {stats?.completedJobsToday || 0} completed today
                  </span>
                </div>
              </div>
              <div className="p-4 bg-[#FF8C00]/10 rounded-xl">
                <WrenchScrewdriverIcon className="w-8 h-8 text-[#FF8C00]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#10B981]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {stats?.totalCustomers || 0}
                </p>
                <div className="mt-2 flex items-center">
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600 font-medium">+5</span>
                  <span className="text-sm text-gray-500 ml-1">this week</span>
                </div>
              </div>
              <div className="p-4 bg-[#10B981]/10 rounded-xl">
                <UsersIcon className="w-8 h-8 text-[#10B981]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#F59E0B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Invoices</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {stats?.pendingInvoices || 0}
                </p>
                <div className="mt-2">
                  <span className="text-sm text-orange-600 font-medium">
                    {formatCurrency(467.500)} pending
                  </span>
                </div>
              </div>
              <div className="p-4 bg-[#F59E0B]/10 rounded-xl">
                <DocumentTextIcon className="w-8 h-8 text-[#F59E0B]" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Employees</p>
                <p className="text-xl font-bold text-gray-900">{stats?.totalEmployees || 0}</p>
                <p className="text-xs text-gray-500 mt-1">2 on duty</p>
              </div>
              <UserGroupIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Today's Appointments</p>
                <p className="text-xl font-bold text-gray-900">{stats?.todayAppointments || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Next at 2:00 PM</p>
              </div>
              <CalendarIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Avg Service Time</p>
                <p className="text-xl font-bold text-gray-900">{stats?.averageServiceTime || 0}h</p>
                <p className="text-xs text-green-500 mt-1">-15 mins vs last week</p>
              </div>
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Customer Rating</p>
                <p className="text-xl font-bold text-gray-900">⭐ {stats?.customerSatisfaction || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Based on 45 reviews</p>
              </div>
              <BellIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderRevenueChart()}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderServiceChart()}
            </div>
          </div>
        </div>

        {/* Employee Performance Chart */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="h-64">
            {renderEmployeePerformance()}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 text-[#008B8B] mr-2" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/dashboard/job-cards')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#008B8B] hover:scale-105"
            >
              <WrenchScrewdriverIcon className="w-8 h-8 text-[#008B8B] mb-3" />
              <h3 className="font-semibold text-gray-900">New Job Card</h3>
              <p className="text-sm text-gray-600 mt-1">Create a new service job</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/customers')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#FF8C00] hover:scale-105"
            >
              <UsersIcon className="w-8 h-8 text-[#FF8C00] mb-3" />
              <h3 className="font-semibold text-gray-900">Add Customer</h3>
              <p className="text-sm text-gray-600 mt-1">Register new customer</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/invoices')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#10B981] hover:scale-105"
            >
              <DocumentTextIcon className="w-8 h-8 text-[#10B981] mb-3" />
              <h3 className="font-semibold text-gray-900">Create Invoice</h3>
              <p className="text-sm text-gray-600 mt-1">Generate new invoice</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/inventory')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#F59E0B] hover:scale-105"
            >
              <CubeIcon className="w-8 h-8 text-[#F59E0B] mb-3" />
              <h3 className="font-semibold text-gray-900">Check Inventory</h3>
              <p className="text-sm text-gray-600 mt-1">View stock levels</p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <button 
                onClick={() => navigate('/dashboard/activity-log')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-3 pb-4 border-b last:border-0"
                >
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">{activity.timestamp}</p>
                      {activity.amount && (
                        <span className="text-sm font-medium text-[#008B8B]">
                          {formatCurrency(activity.amount)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Appointments */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Appointments</h2>
              <button 
                onClick={() => navigate('/dashboard/appointments')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {appointment.time}
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {appointment.duration}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{appointment.customerName}</p>
                  <p className="text-sm text-gray-600">{appointment.vehicle}</p>
                  <p className="text-sm text-[#008B8B] mt-1">{appointment.service}</p>
                </div>
              ))}
              {appointments.length === 0 && (
                <p className="text-center text-gray-500 py-8">No appointments scheduled for today</p>
              )}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Performance Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Revenue Target Progress</p>
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-[#008B8B] h-4 rounded-full" 
                    style={{ width: '75%' }}
                  ></div>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  {formatCurrency(8750.250)} / {formatCurrency(12000)} (75%)
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Customer Retention Rate</p>
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-[#10B981] h-4 rounded-full" 
                    style={{ width: '89%' }}
                  ></div>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  89% returning customers
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Service Completion Rate</p>
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-[#F59E0B] h-4 rounded-full" 
                    style={{ width: '94%' }}
                  ></div>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  94% on-time completion
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GarageOwnerDashboard;